import type {
  CalendarMonthPlan,
  CalendarMonthPlanWithSlots,
  CalendarPlanSlot,
} from "@/types/contentCalendar.type";
import {
  PLAN_FEEDBACK_STATUSES,
  PLAN_IDEATION_STATUSES,
  PLAN_MACHINE_STATUSES,
  isPlanTerminal,
  openDeclineFor,
} from "@/types/contentCalendar.type";
import {
  POLL_FEEDBACK_MS,
  POLL_IDLE_MS,
  POLL_MACHINE_MS,
} from "@/lib/realtime";
import { researchStalled } from "../slotStage";
import { publicationIntegrityOf } from "../publicationIntegrity";

/**
 * The poll predicates for every content-calendar query, in one module so the
 * badge renderer and the poll cadence can never disagree again. The invariant:
 * a non-ARCHIVED month NEVER stops polling — the tiers only decide how fast.
 * The reported bug this replaces: slots showing "Queued" (PLANNED in an
 * approved month, waiting behind the 2-at-a-time ideation fan-out) matched no
 * poll clause, so the board froze until reload while the agents finished.
 */

/**
 * How long a queue state earns the machine tier after the plan last moved.
 * The fan-outs claim within moments and the recovery sweep within 10 minutes;
 * past this window nothing is coming until a cron or a person acts, so the
 * idle floor takes over. Without it, a parked CLIENT_APPROVED month (the
 * pre-2026-07-28 back catalogue the brief sweep never dispatches) or a
 * crashed fan-out's PLANNED slots would fast-poll at 5s for days.
 */
const MACHINE_WINDOW_MS = 30 * 60_000;

function withinMachineWindow(updatedAt: string): boolean {
  return Date.now() - new Date(updatedAt).getTime() < MACHINE_WINDOW_MS;
}

/**
 * GENERATING is unconditionally machine — the backend's 45-min stale sweep
 * reverts it, so fast polling is bounded. The legacy month-unit statuses are
 * still listed for rows written before the per-slot regime.
 */
function planMachineNow(plan: { status: string; updatedAt: string }): boolean {
  return (PLAN_MACHINE_STATUSES as string[]).includes(plan.status);
}

function slotMachineWork(slot: CalendarPlanSlot): boolean {
  return slot.status === "GENERATING_IDEAS" || (slot.ideas ?? []).some((i) => i.status === "WORKING");
}

/**
 * A slot a just-planned month's fan-out may still owe ideas to (it runs two at a
 * time). Bounded by the machine window below: past it nothing is coming on its
 * own — concepts are otherwise a person's Generate.
 */
function slotQueuedForIdeas(slot: CalendarPlanSlot): boolean {
  return slot.status === "PLANNED" && !openDeclineFor(slot) && !researchStalled(slot);
}

/**
 * A post the client just approved, whose production brief has been dispatched
 * but whose row may not exist yet. The per-slot twin of the old
 * CLIENT_APPROVED month window, and bounded the same way — past the window
 * nothing is coming until a cron or a person acts.
 */
function slotAwaitingBriefDispatch(slot: CalendarPlanSlot): boolean {
  return slot.status === "CLIENT_APPROVED" && withinMachineWindow(slot.updatedAt);
}

/** Archived months can still contain future scheduled posts or a dispatcher
 * that is publishing/retrying now. Their record stays live until those current
 * targets settle; historical rows from removed channels do not hold the poll. */
function publicationPollMs(slots: CalendarPlanSlot[]): number | false {
  let scheduled = false;
  for (const slot of slots) {
    if (
      slot.deliverable?.status === "UPLOADING" ||
      slot.deliverable?.status === "PROCESSING"
    ) {
      return POLL_MACHINE_MS;
    }
    const publication = publicationIntegrityOf(slot);
    if (
      publication.retryingChannels.length > 0 ||
      publication.channels.some((channel) => channel.publicationStatus === "PUBLISHING")
    ) {
      return POLL_MACHINE_MS;
    }
    if (
      publication.channels.some((channel) => channel.publicationStatus === "SCHEDULED")
    ) {
      scheduled = true;
    }
  }
  return scheduled ? POLL_IDLE_MS : false;
}

/**
 * Whose turn it is now lives on the SLOTS, not the month: a month sits at
 * APPROVED for the rest of its life while its posts research, go to the client,
 * come back and get produced. So every tier below asks the posts.
 */
export function planPollMs(plan: CalendarMonthPlanWithSlots | undefined): number | false {
  if (!plan) return false;
  if (planMachineNow(plan)) return POLL_MACHINE_MS;
  if (plan.slots.some(slotMachineWork)) return POLL_MACHINE_MS;
  if (plan.slots.some(slotAwaitingBriefDispatch)) return POLL_MACHINE_MS;
  if (
    (PLAN_IDEATION_STATUSES as string[]).includes(plan.status) &&
    plan.slots.some(slotQueuedForIdeas) &&
    withinMachineWindow(plan.updatedAt)
  ) {
    return POLL_MACHINE_MS;
  }
  // Posts with the client: their decisions land server-side and move the board
  // without anyone here touching anything.
  if (plan.slots.some((s) => s.status === "IN_CLIENT_REVIEW")) return POLL_FEEDBACK_MS;
  if ((PLAN_FEEDBACK_STATUSES as string[]).includes(plan.status)) return POLL_FEEDBACK_MS;
  const publicationMs = publicationPollMs(plan.slots);
  if (publicationMs !== false) return publicationMs;
  return isPlanTerminal(plan.status) ? false : POLL_IDLE_MS;
}

/**
 * The month list always at least idles: the 01:00 auto-planner can create a
 * plan for a month this tab still shows as "Not planned", and client decisions
 * flip statuses the list renders in every month's controls.
 */
export function plansListPollMs(plans: CalendarMonthPlan[] | undefined): number {
  if (plans?.some(planMachineNow)) return POLL_MACHINE_MS;
  if (plans?.some((p) => (PLAN_FEEDBACK_STATUSES as string[]).includes(p.status))) {
    return POLL_FEEDBACK_MS;
  }
  return POLL_IDLE_MS;
}

/**
 * Month briefs. CLIENT_APPROVED/BRIEFS_GENERATING poll fast even with zero
 * GENERATING rows: the fan-out claims the plan BEFORE creating the brief rows,
 * and a fetch landing in that window used to stop the poll for good. (No
 * machine window here — the payload carries no plan timestamp, and this query
 * only runs while the brief board is open.)
 */
export function planBriefsPollMs(
  data: { planStatus: string; briefs: { status: string }[] } | undefined,
): number | false {
  if (!data) return false;
  if ((PLAN_MACHINE_STATUSES as string[]).includes(data.planStatus)) return POLL_MACHINE_MS;
  if (data.briefs.some((b) => b.status === "GENERATING")) return POLL_MACHINE_MS;
  if (data.briefs.some((b) => b.status === "IN_REVIEW")) return POLL_FEEDBACK_MS;
  if ((PLAN_FEEDBACK_STATUSES as string[]).includes(data.planStatus)) return POLL_FEEDBACK_MS;
  return isPlanTerminal(data.planStatus) ? false : POLL_IDLE_MS;
}

/**
 * One slot's brief. `brief: null` is ambiguous on its own — it can mean "no
 * brief is coming" or "one was just dispatched and the row does not exist for
 * another second". The SLOT's status is what separates them, because brief
 * generation is dispatched the moment the client approves that post: a
 * CLIENT_APPROVED slot with no brief row IS the queue state, and it is what
 * used to freeze the workroom on "Awaiting brief" forever.
 *
 * `planStatus` still decides only whether to stop entirely (archived month).
 */
export function slotBriefPollMs(
  data: { brief: { status: string } | null } | undefined,
  planStatus: string | undefined,
  slotStatus?: string,
): number | false {
  const status = data?.brief?.status;
  if (status === "GENERATING") return POLL_MACHINE_MS;
  // Picked or approved with no brief row yet: Creo is about to write it.
  if (!data?.brief && (slotStatus === "SIGNED_OFF" || slotStatus === "CLIENT_APPROVED")) return POLL_MACHINE_MS;
  if (status === "IN_REVIEW") return POLL_FEEDBACK_MS;
  if (!planStatus || isPlanTerminal(planStatus)) return false;
  return POLL_IDLE_MS;
}

/**
 * Brief review rounds: client decisions land server-side while a round is
 * PENDING; otherwise idle — a teammate can open the next round from another
 * tab. Stops only with the plan.
 */
export function briefReviewPollMs(
  data: { planStatus: string; rounds: { status: string }[] } | undefined,
): number | false {
  if (!data) return false;
  if (data.rounds.some((r) => r.status === "PENDING")) return POLL_FEEDBACK_MS;
  return isPlanTerminal(data.planStatus) ? false : POLL_IDLE_MS;
}
